import { Command } from 'commander';
import pc from 'picocolors';
import inquirer from 'inquirer';
import { existsSync, writeFileSync, mkdirSync } from 'fs';
import { join, relative } from 'path';
import { detectFramework } from '../utils/framework-detector';
import { generateAllTemplates } from '../utils/template-integration';
import { validateProjectStructure } from '../utils/validators';
import { MultiStepProgress } from '../utils/progress';
import { handleError, createValidationError, createCommandError } from '../utils/errors';
import { toError } from '../utils/error-type-guards';
import { getOutput } from '../utils/output';
import { TransactionManager } from '../utils/transaction-manager';
import { NextConfigUpdater } from '../utils/next-config-updater';
import { EnvGenerator } from '../utils/env-generator';
import { detectLayoutFile } from '../utils/layout-detector';
import { injectProvider } from '../utils/layout-injector';

export const initCommand = new Command('init')
  .description('Initialize Vibecode DevTools in your Next.js project')
  .option('-l, --level <level>', 'setup level: quick, standard, or advanced', 'quick')
  .option('-y, --yes', 'skip confirmation prompts')
  .option('-f, --force', 'overwrite existing files')
  .option('--app-dir <path>', 'specify app directory path', 'app')
  .option('--src-dir', 'use src directory structure')
  .action(async (options) => {
    // Check if using simplified initialization
    if (options.level === 'quick' || options.level === 'standard') {
      const { performQuickSetup, performStandardSetup } = await import('./simple-init');
      const pc = (await import('picocolors')).default;
      const { detectFramework } = await import('../utils/framework-detector');
      const { getErrorMessage } = await import('../utils/error-type-guards');
      
      console.log(pc.cyan('\n🚀 Vibeship DevTools - Simplified Setup\n'));
      
      try {
        const framework = await detectFramework();
        
        if (!framework.isNextJs) {
          console.log(pc.red('❌ This command requires a Next.js project'));
          console.log(pc.dim('   Please run this in a Next.js project directory\n'));
          process.exit(1);
        }

        console.log(pc.green(`✓ Detected Next.js ${framework.version} (${framework.isAppRouter ? 'App' : 'Pages'} Router)`));

        const startTime = Date.now();
        
        if (options.level === 'quick') {
          await performQuickSetup(framework, options);
        } else {
          await performStandardSetup(framework, options);
        }

        const duration = Date.now() - startTime;
        console.log(pc.green(`\n✅ Setup completed in ${duration}ms!`));
        
        // Show next steps
        console.log(pc.cyan('\n📋 Next Steps:\n'));
        console.log(pc.white('1. Install the UI package:'));
        console.log(pc.dim('   npm install @vibeship/ui\n'));
        console.log(pc.white('2. Start your development server:'));
        console.log(pc.dim('   npm run dev\n'));
        console.log(pc.cyan('🎉 You\'re all set!\n'));
        
        return;
      } catch (error) {
        console.log(pc.red('\n❌ Setup failed:'));
        console.log(pc.red(`   ${getErrorMessage(error)}\n`));
        process.exit(1);
      }
    }

    const output = getOutput({ verbose: options.verbose });
    output.banner('Initializing Vibeship DevTools');

    const steps = [
      'Detecting project structure',
      'Validating project setup',
      'Configuring features',
      'Generating template files',
      'Creating configuration file',
      'Setting up API routes',
      'Injecting provider into layout',
      'Updating next.config.js',
      'Creating environment files',
      'Updating package.json',
      'Finalizing setup'
    ];

    const progress = new MultiStepProgress(steps, options.verbose);
    const transaction = new TransactionManager();
    
    try {
      progress.start();
      
      const framework = await detectFramework();
      
      if (!framework.isNextJs) {
        progress.fail('This doesn\'t appear to be a Next.js project');
        throw createValidationError(
          'Not a Next.js project',
          {
            command: 'init',
            suggestion: 'Please run this command in a Next.js project directory'
          }
        );
      }

      progress.updateCurrentStep(`Detected Next.js ${framework.version} (${framework.isAppRouter ? 'App' : 'Pages'} Router)`);

      // Validate project structure
      progress.next('Validating project setup');
      const validation = await validateProjectStructure(framework);
      if (!validation.isValid) {
        progress.warn('Project structure issues found');
        output.warning('Project structure issues found:');
        validation.errors.forEach(err => output.list([err], '•'));
        
        if (!options.force) {
          const { proceed } = await inquirer.prompt([{
            type: 'confirm',
            name: 'proceed',
            message: 'Continue anyway?',
            default: false
          }]);
          
          if (!proceed) {
            throw createValidationError('Setup cancelled by user');
          }
        }
      }

      // Configuration questions
      progress.next('Configuring features');
      const config = await promptConfiguration(options, framework);

      // Now advance to step 4 after configuration is complete
      progress.next('Generating template files');
      
      const generatedFiles = await generateAllTemplates({
        ...config,
        scanPaths: config.scanPaths,
        include: config.include,
        exclude: config.exclude,
        writeFiles: false,
      });
      
      // Add all file operations to transaction
      generatedFiles.forEach(file => {
        transaction.addCreateOperation(file.path, file.content);
      });
      
      // Add .vibeship directory creation
      transaction.addCreateOperation('.vibeship/.gitkeep', '');
      transaction.addCreateOperation('.vibeship/cache/.gitkeep', '');
      
      // Add .tickets directory with example content
      const exampleTicketContent = `# Example Task

This is an example task file to demonstrate how Vibeship DevTools detects and tracks tasks.

## TODO: Complete the setup
Make sure to configure your API keys in the .env.local file.

## TODO: Customize your workflow
- [ ] Update the vibeship.config.ts file with your preferred settings
- [ ] Add more markdown files to track tasks
- [ ] Try the AI chat feature by clicking the floating button

## FIXME: Review the documentation
Check out the official documentation at https://docs.vibeship.dev for more information.

## NOTE: Task patterns
Vibeship DevTools automatically detects these patterns:
- TODO: For tasks that need to be done
- FIXME: For bugs or issues to fix
- HACK: For temporary solutions
- NOTE: For important notes

Happy coding! 🚀
`;
      
      transaction.addCreateOperation('.tickets/example-task.md', exampleTicketContent);
      transaction.addCreateOperation('.tickets/.gitkeep', '');

      // Execute all operations in a transaction
      const result = await transaction.execute(async () => {
        // Files will be written by transaction manager
        
        // Inject provider into layout
        progress.next('Setting up API routes');
        const layoutInfo = await detectLayoutFile(framework);
        const configPath = config.typescript !== false ? './vibeship.config' : './vibeship.config.js';
        
        let providerInjected = false;
        
        if (!layoutInfo.exists || !options.force) {
          // Create or modify layout file
          const injectionResult = await injectProvider(layoutInfo, configPath);
          
          if (injectionResult.success && injectionResult.modifiedContent) {
            // Add layout file creation/modification to transaction
            transaction.addCreateOperation(layoutInfo.path, injectionResult.modifiedContent);
            output.success(`Provider injected into ${layoutInfo.path}`);
            providerInjected = true;
          } else if (!injectionResult.success) {
            output.warning(`Could not inject provider automatically: ${injectionResult.message}`);
            output.info('Please add VibeshipProvider manually - see docs for instructions');
          } else {
            // Provider already exists
            providerInjected = true;
          }
        }
        
        // Update next.config.js
        progress.next();
        try {
          const nextConfigUpdater = new NextConfigUpdater(process.cwd());
          await nextConfigUpdater.update({
            experimental: {
              // Add any experimental features needed
            },
            transpilePackages: ['@vibeship/ui'],
          });
        } catch (error) {
          output.warning('Could not update next.config.js automatically');
          output.info('Please add "@vibeship/ui" to transpilePackages manually');
        }

        // Create environment files
        progress.next('Creating environment files');
        const envGenerator = new EnvGenerator({
          ai: config.features?.includes('ai'),
          authentication: config.security?.authentication,
        });
        const envPath = envGenerator.generate(process.cwd());
        transaction.addCreateOperation(envPath, ''); // Already written by generator

        // Update package.json
        progress.next('Updating package.json');
        updatePackageJson(config);

        progress.next('Finalizing setup');
        // Add a small delay to show the final step
        await new Promise(resolve => setTimeout(resolve, 500));

        return { generatedFiles, envPath, providerInjected };
      });

      progress.complete('Vibeship DevTools initialized successfully!');
      
      output.section('Next steps:');
      const nextSteps = [
        'Install dependencies: npm install @vibeship/ui',
        'Configure environment variables in .env.local',
      ];
      
      // Only show manual provider step if injection failed
      if (!result.providerInjected) {
        nextSteps.push(
          'Add the provider to your layout:',
          '  // app/layout.tsx or pages/_app.tsx',
          '  import { VibeshipProvider, VibeshipDevTools } from "@vibeship/devtools"',
          '  <VibeshipProvider config={vibeshipConfig}>',
          '    {children}',
          '    <VibeshipDevTools />',
          '  </VibeshipProvider>'
        );
      }
      
      nextSteps.push('Start your development server and enjoy!');
      output.list(nextSteps);
      
      output.info(`Generated ${result.generatedFiles.length} files`);
      output.info('Created .env.example with configuration options');
      output.info('For more information: https://docs.vibeship.dev');

    } catch (error) {
      progress.fail('Initialization failed');
      handleError(toError(error), options.verbose);
    }
  });

async function promptConfiguration(options: any, framework: any) {
  if (options.yes) {
    return {
      framework,
      srcDir: options.srcDir || framework.hasSrcDir,
      appDir: options.appDir,
      scanPaths: ['./docs', './README.md', './.tickets'],
      include: ['**/*.md', '**/*.mdx'],
      exclude: ['**/node_modules/**', '**/.next/**'],
      features: ['tasks', 'ai', 'realtime', 'commandPalette'],
      security: {
        authentication: false,
        rateLimit: true,
        cors: true,
      },
      typescript: framework.hasTypeScript,
    };
  }

  const answers = await inquirer.prompt([
    {
      type: 'input',
      name: 'scanPaths',
      message: 'Which directories should we scan for markdown files?',
      default: './docs,./README.md,./.tickets',
      filter: (input: string) => input.split(',').map(p => p.trim()),
    },
    {
      type: 'checkbox',
      name: 'features',
      message: 'Which features would you like to enable?',
      choices: [
        { name: 'Task extraction (TODO/FIXME)', value: 'tasks', checked: true },
        { name: 'AI-powered chat', value: 'ai', checked: true },
        { name: 'Real-time updates', value: 'realtime', checked: true },
        { name: 'Command palette', value: 'commandPalette', checked: true },
      ],
    },
    {
      type: 'checkbox',
      name: 'security',
      message: 'Which security features would you like to enable?',
      choices: [
        { name: 'Authentication middleware', value: 'authentication', checked: false },
        { name: 'Rate limiting', value: 'rateLimit', checked: true },
        { name: 'CORS configuration', value: 'cors', checked: true },
      ],
    },
    {
      type: 'confirm',
      name: 'typescript',
      message: 'Use TypeScript for generated files?',
      default: framework.hasTypeScript,
    },
  ]);

  // Transform security array to object
  const security = answers.security ? {
    authentication: answers.security.includes('authentication'),
    rateLimit: answers.security.includes('rateLimit'),
    cors: answers.security.includes('cors'),
  } : {
    authentication: false,
    rateLimit: true,
    cors: true,
  };
  
  return {
    framework,
    srcDir: options.srcDir || framework.hasSrcDir,
    appDir: options.appDir,
    ...answers,
    security,
  };
}


function updatePackageJson(config: any) {
  const packageJsonPath = join(process.cwd(), 'package.json');
  
  if (existsSync(packageJsonPath)) {
    try {
      const packageJson = JSON.parse(readFileSync(packageJsonPath, 'utf-8'));
      
      // Add scripts if they don't exist
      if (!packageJson.scripts) {
        packageJson.scripts = {};
      }
      
      if (!packageJson.scripts['vibecode:cache']) {
        packageJson.scripts['vibeship:cache'] = 'vibeship cache:clear';
      }
      
      writeFileSync(packageJsonPath, JSON.stringify(packageJson, null, 2) + '\n');
    } catch (error) {
      // Silently fail if we can't update package.json
    }
  }
}

// Add missing import
import { readFileSync } from 'fs';